import { formatDurationFromSeconds } from '@heidi/utils/dates';
import type { IntlShape } from 'react-intl';

import {
  patientNameKey,
  patientNameWithoutDemographics,
} from './patient-display-name-internal';
import {
  hasPatientNameFields,
  resolvePatientLabel,
  type PatientNameFields,
} from './patientListName';

const PLACEHOLDER_SESSION_NAME =
  /^(?:(?:unknown|untitled)(?:\s+(?:session|visit))?|session|visit)$/iu;

export interface SessionTokenLabelSource {
  readonly session_name?: string | null;
  readonly created_at?: string | null;
  readonly created_time_local?: string | null;
  readonly ehr_appt_id?: string | null;
  readonly patient?: { readonly name?: string | null } | null;
  readonly patient_profile?: PatientNameFields | null;
}

export const sessionTitleNamesPatient = (
  title: string | undefined,
  patientName: string | undefined
): boolean =>
  !!title && !!patientName && patientNameKey(title) === patientNameKey(patientName);

const sessionTitle = (
  storedName: string | undefined,
  patientName: string | undefined,
  profile: PatientNameFields | undefined
): string | undefined => {
  if (!storedName || PLACEHOLDER_SESSION_NAME.test(storedName)) return undefined;
  const namesPatient =
    sessionTitleNamesPatient(storedName, patientName) ||
    storedName.toLowerCase() === profile?.demographic_string?.trim().toLowerCase();
  return namesPatient ? patientNameWithoutDemographics(storedName) : storedName;
};

export type SessionTokenIdentity = {
  readonly title?: string;
  readonly patientName?: string;
  readonly day: string;
  readonly time: string;
  readonly dayTime: string;
};

type ParseSessionTimestamp = (time: string | null | undefined, isUtc: boolean) => Date;

const DAY_FORMAT = { day: 'numeric', month: 'short', year: 'numeric' } as const;
const TIME_FORMAT = { hour: 'numeric', minute: '2-digit' } as const;

const sessionCreatedAt = (
  session: SessionTokenLabelSource,
  parseTimestamp: ParseSessionTimestamp
): Date =>
  parseTimestamp(session.created_time_local ?? session.created_at, !session.ehr_appt_id);

export const sessionTokenIdentity = (
  session: SessionTokenLabelSource,
  intl: IntlShape,
  parseTimestamp: ParseSessionTimestamp
): SessionTokenIdentity => {
  const profile = hasPatientNameFields(session.patient_profile)
    ? session.patient_profile
    : undefined;
  const patientName = resolvePatientLabel(profile, session.patient?.name);
  const title = sessionTitle(session.session_name?.trim(), patientName, profile);
  const createdAt = sessionCreatedAt(session, parseTimestamp);
  return {
    ...(title ? { title } : {}),
    ...(patientName ? { patientName } : {}),
    day: intl.formatDate(createdAt, DAY_FORMAT),
    time: intl.formatTime(createdAt, TIME_FORMAT),
    dayTime: intl.formatDate(createdAt, { ...DAY_FORMAT, ...TIME_FORMAT }),
  };
};

export const sessionTokenLabel = ({
  title,
  day,
  dayTime,
}: SessionTokenIdentity): string => (title ? `${title} · ${day}` : dayTime);

/** A day an object was last touched (seen, edited), or nothing when it has none. */
export const objectDayLabel = (
  value: string | null | undefined,
  intl: IntlShape,
  parseTimestamp: ParseSessionTimestamp
): string | undefined =>
  // The parse falls back to "now", so a missing value never reaches it.
  value ? intl.formatDate(parseTimestamp(value, true), DAY_FORMAT) : undefined;

export const sessionDurationLabel = (
  seconds: number | null | undefined
): string | undefined =>
  seconds && seconds > 0
    ? formatDurationFromSeconds(seconds, { shorten: true })
    : undefined;
